import { useState, useEffect, useRef } from 'react'
import { VITE_API_BASE } from '../config/apiConfig'

const API = VITE_API_BASE

const ASSET_TYPES = [
  { type: '全部', extensions: [] },
  { type: '图片', extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'] },
  { type: '视频', extensions: ['mp4', 'webm', 'mov'] },
  { type: '音频', extensions: ['mp3', 'wav', 'aac', 'm4a'] },
  { type: '文档', extensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx'] },
]

export default function AssetManagement() {
  const [type, setType] = useState('全部')
  const [assets, setAssets] = useState([])
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [domain, setDomain] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef(null)

  useEffect(() => {
    loadAssets()
    loadDomain()
  }, [])

  const loadDomain = async () => {
    try {
      const res = await fetch(`${API}/upload/token`)
      const d = await res.json()
      if (d.success) {
        setDomain(d.data.domain)
      }
    } catch (e) {
      console.log('使用默认域名')
    }
  }

  const loadAssets = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API}/materials`)
      const d = await res.json()
      if (d.success) {
        setAssets(d.data || [])
      } else {
        // 使用演示数据
        setAssets([
          { id: 1, name: 'banner_home.jpg', type: '图片', size: '2.4MB', url: '', createdAt: '2026-04-15' },
          { id: 2, name: 'logo_primary.png', type: '图片', size: '156KB', url: '', createdAt: '2026-04-12' },
          { id: 3, name: 'bg_music_01.mp3', type: '音频', size: '3.2MB', url: '', createdAt: '2026-04-10' },
          { id: 4, name: 'promo_video.mp4', type: '视频', size: '45MB', url: '', createdAt: '2026-04-08' },
        ])
      }
    } catch (e) {
      setAssets([
        { id: 1, name: 'banner_home.jpg', type: '图片', size: '2.4MB', url: '', createdAt: '2026-04-15' },
        { id: 2, name: 'logo_primary.png', type: '图片', size: '156KB', url: '', createdAt: '2026-04-12' },
      ])
    }
    setLoading(false)
  }

  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase()
    for (const t of ASSET_TYPES) {
      if (t.extensions.includes(ext)) {
        return t.type
      }
    }
    return '文档'
  }

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB'
    return (bytes / 1024 / 1024 / 1024).toFixed(1) + ' GB'
  }

  const uploadFile = async (file) => {
    setUploading(true)
    setUploadProgress(0)
    
    try {
      // 获取上传凭证
      const tokenRes = await fetch(`${API}/upload/token`)
      const tokenData = await tokenRes.json()
      if (!tokenData.success) {
        alert('获取上传凭证失败')
        setUploading(false)
        return
      }
      
      const { token, domain: uploadDomain } = tokenData.data
      setDomain(uploadDomain)
      
      // 构建formData
      const formData = new FormData()
      formData.append('file', file)
      formData.append('token', token)
      formData.append('key', `assets/${Date.now()}_${file.name}`)
      
      // 上传到七牛云
      const res = await fetch('https://up-z2.qiniup.com', {
        method: 'POST',
        body: formData,
      })
      
      const result = await res.json()
      
      if (result.key) {
        const url = `${uploadDomain}/${result.key}`
        
        // 保存到本地数据库
        await fetch(`${API}/materials`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: file.name,
            url: url,
            type: getFileType(file.name),
            size: formatSize(file.size),
            size_bytes: file.size,
            key: result.key
          })
        })
        
        setUploadProgress(100)
        loadAssets()
        alert('上传成功！')
      } else {
        alert('上传失败')
      }
    } catch (e) {
      console.error('上传错误:', e)
      alert('上传失败: ' + e.message)
    }
    
    setUploading(false)
  }

  const handleFileSelect = (e) => {
    const files = e.target.files
    if (files && files.length > 0) {
      uploadFile(files[0])
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      uploadFile(files[0])
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = () => {
    setDragOver(false)
  }

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url)
    alert('链接已复制！')
  }

  const handleDelete = async (asset) => {
    if (!confirm(`确定删除 "${asset.name}" 吗？`)) return
    
    try {
      // 如果有key，删除七牛云文件
      if (asset.key) {
        await fetch(`${API}/upload/${asset.key}`, { method: 'DELETE' })
      }
      // 删除数据库记录
      await fetch(`${API}/materials/${asset.id}`, { method: 'DELETE' })
      loadAssets()
      alert('已删除')
    } catch (e) {
      alert('删除失败')
    }
  }

  const filtered = type === '全部' ? assets : assets.filter(a => a.type === type)
  const totalSize = assets.reduce((s, a) => s + (a.size_bytes || 0), 0)
  const counts = ASSET_TYPES.map(t => ({
    ...t,
    count: t.type === '全部' ? assets.length : assets.filter(a => a.type === t.type).length
  }))

  const getIcon = (type) => {
    switch (type) {
      case '图片': return '🖼️'
      case '视频': return '🎬'
      case '音频': return '🎵'
      default: return '📄'
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 20, marginBottom: 4 }}>📁 素材管理</h2>
          <p style={{ color: '#666', fontSize: 13 }}>管理图片、视频、音频等素材，已连接七牛云存储</p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          style={{ 
            padding: '12px 24px', 
            background: uploading ? '#d9d9d9' : '#1890ff', 
            color: '#fff', 
            border: 'none', 
            borderRadius: 6, 
            cursor: uploading ? 'not-allowed' : 'pointer', 
            fontSize: 14 
          }}
        >
          {uploading ? `上传中... ${uploadProgress}%` : '+ 上传素材'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>

      {/* Type Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {counts.map(t => (
          <button
            key={t.type}
            onClick={() => setType(t.type)}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: 20,
              cursor: 'pointer',
              fontSize: 13,
              background: type === t.type ? '#1890ff' : '#f5f5f5',
              color: type === t.type ? '#fff' : '#666'
            }}
          >
            {t.type} ({t.count})
          </button>
        ))}
      </div>

      {/* Upload Area */}
      {uploading && (
        <div style={{ 
          border: `2px dashed ${dragOver ? '#52c41a' : '#1890ff'}`, 
          borderRadius: 8, 
          padding: 40, 
          textAlign: 'center', 
          marginBottom: 20, 
          background: '#f0f2f5',
          transition: 'all 0.3s'
        }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>📤</div>
          <div style={{ color: '#666', marginBottom: 8 }}>正在上传到七牛云...</div>
          <div style={{ 
            width: '60%', 
            height: 8, 
            background: '#e8e8e8', 
            borderRadius: 4, 
            margin: '16px auto',
            overflow: 'hidden'
          }}>
            <div style={{ 
              width: `${uploadProgress}%`, 
              height: '100%', 
              background: '#1890ff',
              transition: 'width 0.3s'
            }} />
          </div>
          <div style={{ color: '#999', fontSize: 12 }}>支持 JPG、PNG、MP4、MP3、PDF 格式，单个文件不超过 100MB</div>
        </div>
      )}

      {/* Drop Zone (when not uploading) */}
      {!uploading && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? '#52c41a' : '#d9d9d9'}`,
            borderRadius: 8,
            padding: 24,
            textAlign: 'center',
            marginBottom: 20,
            background: dragOver ? '#f6ffed' : '#fafafa',
            cursor: 'pointer',
            transition: 'all 0.3s'
          }}
        >
          <div style={{ color: '#666', fontSize: 14 }}>
            📎 拖拽文件到此处，或点击选择文件
          </div>
          <div style={{ color: '#999', fontSize: 12, marginTop: 8 }}>
            支持图片、视频、音频、PDF、Word、Excel 格式
          </div>
        </div>
      )}

      {/* Storage Info */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        padding: '12px 16px', 
        background: '#e6f7ff', 
        borderRadius: 8, 
        marginBottom: 20,
        fontSize: 13
      }}>
        <span>📊 存储统计</span>
        <span>总计 {assets.length} 个文件，约 {formatSize(totalSize)}</span>
        <span style={{ color: '#52c41a' }}>✅ 七牛云已连接</span>
      </div>

      {/* Asset Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>加载中...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
          {filtered.map(a => (
            <div key={a.id} style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ height: 120, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <span style={{ fontSize: 40 }}>{getIcon(a.type)}</span>
                {a.type === '图片' && a.url && (
                  <img 
                    src={a.url} 
                    alt={a.name}
                    style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none' }}
                  />
                )}
              </div>
              <div style={{ padding: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#999', marginTop: 4 }}>
                  <span>{a.size || formatSize(a.size_bytes || 0)}</span>
                  <span>{a.createdAt}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button 
                    onClick={() => a.url && handleCopy(a.url)}
                    style={{ flex: 1, padding: '4px 8px', background: '#f5f5f5', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}
                  >
                    复制链接
                  </button>
                  <button 
                    onClick={() => handleDelete(a)}
                    style={{ padding: '4px 8px', background: '#fff', border: '1px solid #ff4d4f', borderRadius: 4, cursor: 'pointer', fontSize: 11, color: '#ff4d4f' }}
                  >
                    删除
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filtered.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📂</div>
          <div>暂无素材</div>
          <div style={{ fontSize: 12, marginTop: 8 }}>点击上方按钮上传第一个素材</div>
        </div>
      )}
    </div>
  )
}
